import { yupResolver } from '@hookform/resolvers/yup';
import { Close as CloseIcon } from '@mui/icons-material';
import { Dialog } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FlexButton } from 'v3/shared/components/Button/FlexButton';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { IVerificationModalProps } from 'v3/shared/components/Modal/SellerVerificationModal/types';
import { SellerCodeSchema } from 'v3/shared/schemas/codeSeller';

import {
  AcceptanceBox,
  AlertContainer,
  CheckboxContainer,
  confirmationDialogPaperPropsStyle,
  FormSection,
  NumberCircle,
  SectionContent,
  SpaceBetween,
  StyledButtonContainer,
  StyledCheckboxTerms,
  StyledDialogContent,
  StyledErrorIcon,
  StyledIconButton,
  SubtitleMediumSpan,
  TagSpaceInputAndCpf,
  TextContainer,
  TitleContainer,
  TitleMediumSpan,
} from 'v3/shared/components/Modal/SellerVerificationModal/styles';

import { debounce } from 'utils/debounce';
import { removeCpfFormatting } from 'utils/formatters';
import * as api from 'v3/services/api';
import { BodySmallBold } from 'v3/shared/typography/BodySmall';
import { HeadlineLarge } from 'v3/shared/typography/HeadlineLarge';
import { LabelSmall, LabelSmallBold } from 'v3/shared/typography/LabelSmall';
import { TitleSmall } from 'v3/shared/typography/TitleSmall';
import { theme } from 'v3/styles/global-styles';
import { ISeller } from 'v3/data/store/seller/types';
import { useStoreSelector } from 'v3/data/store';

export const SellerVerificationModal: React.FC<IVerificationModalProps> = ({ modal, onClose, onConfirm }) => {
  const {
    getValues,
    control,
    setError,
    formState: { isValid },
    reset,
  } = useForm<{ code: string }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(SellerCodeSchema) as any,
  });

  const [seller, setSeller] = useState<ISeller | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const { id } = useStoreSelector((store) => store.store.storeLogged);

  const DEBOUNCE_DELAY = 1000;
  const VERIFY_NUMBER_CODE = 14;

  const handleCodeChange = async (code: string) => {
    const getCode = getValues('code');

    if (getCode.length === VERIFY_NUMBER_CODE) {
      try {
        const formatedCpf = removeCpfFormatting(code);
        const response = await api.findSeller(formatedCpf, id);
        setSeller(response.data);
      } catch (error) {
        setError('code', { message: 'Consultor não encontrado. Por favor, verifique seu CPF.' });
        console.error('Erro ao buscar consultor:', error);
        setSeller(null);
      }
    } else {
      setSeller(null);
    }
  };

  const debounceHandleSearchSeller = (code: string) => {
    debounce(() => handleCodeChange(code), DEBOUNCE_DELAY, 'handleCodeChange');
  };

  const handleCloseModal = () => {
    setSeller(null);
    setAcceptedTerms(false);
    reset({ code: '' });
    onClose();
  };

  const canProceed = isValid && acceptedTerms && seller !== null;

  return (
    <Dialog
      open={modal.active}
      onClose={handleCloseModal}
      PaperProps={{
        sx: confirmationDialogPaperPropsStyle,
      }}
    >
      <StyledDialogContent>
        <StyledIconButton onClick={handleCloseModal}>
          <CloseIcon />
        </StyledIconButton>
        <HeadlineLarge>{modal.title}</HeadlineLarge>
        <TagSpaceInputAndCpf>
          <FormSection>
            <TitleContainer>
              <NumberCircle>
                <LabelSmallBold>1</LabelSmallBold>
              </NumberCircle>
              <BodySmallBold color={theme.palette.common.white}>
                Identifique-se para acessar os dados do cliente
              </BodySmallBold>
            </TitleContainer>
            <SectionContent>
              <InputFormController
                formControl={control}
                inputType="tel"
                formControlName="code"
                label="CPF do consultor"
                mask="000.000.000-00"
                placeholder="___.___.___-__"
                customMask={TextMaskCustom}
                fullWidth
                onChangeInput={debounceHandleSearchSeller}
              />
            </SectionContent>
          </FormSection>

          <FormSection>
            <TitleContainer>
              <NumberCircle>
                <LabelSmallBold>2</LabelSmallBold>
              </NumberCircle>
              <BodySmallBold color={theme.palette.common.white}>Peça o aceite do cliente</BodySmallBold>
            </TitleContainer>

            <AlertContainer>
              <StyledErrorIcon />
              <TitleSmall color={theme.palette.common.black}>
                Este aceite deve ser realizado exclusivamente pelo cliente. O cliente deve ler e aceitar este termo
                pessoalmente.
              </TitleSmall>
            </AlertContainer>

            <AcceptanceBox>
              <SpaceBetween>
                <TextContainer>
                  <SubtitleMediumSpan>
                    Estou ciente que em caso de informações compartilhadas com terceiros, a{' '}
                    <TitleMediumSpan>responsabilidade será atribuída ao consultor identificado.</TitleMediumSpan>
                  </SubtitleMediumSpan>
                </TextContainer>
                <CheckboxContainer>
                  <StyledCheckboxTerms
                    size="medium"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  <LabelSmall>Eu, cliente, confirmo que li e aceito os termos</LabelSmall>
                </CheckboxContainer>
              </SpaceBetween>
            </AcceptanceBox>
          </FormSection>
          <StyledButtonContainer>
            <TextButton
              data-testid="text-button-cancel"
              size="small"
              text={modal.cancelButtonText}
              handleClick={handleCloseModal}
            />
            <FlexButton
              data-testid="flex-button-confirm"
              size="small"
              text={modal.confirmButtonText}
              handleClick={onConfirm}
              disabled={!canProceed}
            />
          </StyledButtonContainer>
        </TagSpaceInputAndCpf>
      </StyledDialogContent>
    </Dialog>
  );
};